import { registerLocaleData, ViewportScroller } from '@angular/common';

// [i18n] Angular Locales
import localeCs from '@angular/common/locales/cs';
import localeDe from '@angular/common/locales/de';
import localeEnGB from '@angular/common/locales/en-GB';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localeHu from '@angular/common/locales/hu';
import localeJa from '@angular/common/locales/ja';
import localeKo from '@angular/common/locales/ko';
import localePl from '@angular/common/locales/pl';
import localePt from '@angular/common/locales/pt';
import localeRo from '@angular/common/locales/ro';
import localeRu from '@angular/common/locales/ru';
import localeTr from '@angular/common/locales/tr';
import localeCn from '@angular/common/locales/zh';
import localeIt from '@angular/common/locales/it';

registerLocaleData(localeCs);
registerLocaleData(localeDe);
registerLocaleData(localeEnGB);
registerLocaleData(localeEs);
registerLocaleData(localeFr);
registerLocaleData(localeHu);
registerLocaleData(localeJa);
registerLocaleData(localeKo);
registerLocaleData(localePl);
registerLocaleData(localePt);
registerLocaleData(localeRo);
registerLocaleData(localeRu);
registerLocaleData(localeTr);
registerLocaleData(localeCn);
registerLocaleData(localeIt);

import { EventManager } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withInMemoryScrolling, withPreloading, withRouterConfig } from '@angular/router';
import { ValidationErrors } from '@angular/forms';
import { provideState, provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { MissingTranslationHandler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { from, Observable } from 'rxjs';
import {
  SMA_VALIDATION_ERROR_MAPPER,
  SmaValidationErrorContext,
  SmaValidationErrorMapper,
  provideSmaTrustedIframeDomains,
  WEB_PUB_SUB_ENDPOINT,
  WebPubSubService,
} from '@festo-ax/smartenance-shared-ui';
import { CookieService } from 'ng2-cookies';
import { environment } from 'environments/environment';
import { WEB_PUB_SUB_API_ENDPOINT } from './services/utils.service';
import { appRoutes } from './app.routes';
import { AppRoutingPreloadStrategy } from './app-routing-preload.strategy';
import { provideEagerRouteStore } from './state-management/route/route-store.provider';
import { LanguageHeaderInterceptor } from './interceptors/language-header.interceptor';
import { CustomEventManager } from './utils/custom-event-manager';
import { AppConfig } from './app.config';
import { getLocalStorage, getSessionStorage, LOCAL_STORAGE, SESSION_STORAGE } from './services/storage/storage.service';
import { WINDOW, windowFactory } from './utils/window';
import { HttpErrorConvertInterceptor } from './interceptors/http-error-convert-interceptor.service';
import { ApiCacheInterceptor } from './interceptors/api-cache.interceptor';
import { DeletionGuardInterceptor } from './interceptors/deletion-guard.interceptor';
import { CustomMissingTranslationHandler } from './utils/handlers/custom-missing-translation.handler';
import { FormHelperService } from './services/form-helper.service';
import {
  BearerTokenInterceptor,
  CurrentTenantInterceptor,
  initializeDevAuth,
  initializeAuthSessionKeepalive,
  initializeIdentity,
  initializeWelcomeBranding,
  SilentTokenRefreshInterceptor,
  DidomiService,
  FwaTrackingService,
  initializeDidomi,
  initializeDidomiRecovery,
  initializeFwaTracking,
  WelcomeBrandingLoaderService,
} from './standalone-core/features';
import { provideTableMemory } from './state-management/global-table-memory/table-memory.provider';
import { provideFilterMemory } from './state-management/global-filter-memory/filter-memory.provider';
import { provideVersionIntro } from './state-management/version-intro/version-intro.provider';
import { reducers } from './reducers';
import * as customFieldsReducer from './reducers/custom-fields';
import { AttachmentsEffects } from './effects/attachments';
import { CustomFieldsEffects } from './effects/custom-fields';
import { assetsFeature } from './reducers/assets';
import { AssetsEffects } from './effects/assets';
import { RouteSyncEffects } from './effects/route-sync';
import { tenantFeature } from './reducers/tenant';
import { TenantEffects } from './effects/tenant';
import { tableStareDataFeature } from './reducers/table-store-data';
import { TableStoreEffects } from './effects/table-store';
import { SapGlobalEffects } from './effects/sap-global';
import { SapAssetsEffects } from './effects/sap-assets';

export class HashTranslateLoader implements TranslateLoader {
  getTranslation(lang: string): Observable<unknown> {
    /**
     * we exclude use base language for english britain
     */
    if (lang !== 'en-gb' && lang.indexOf('-')) {
      lang = lang.slice(0, 2);
    }
    return from(import(`../assets/i18n/${lang}.json`));
  }
}

export function initializeApp(appConfig: AppConfig): () => Promise<void> {
  return () => appConfig.load();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    provideAnimations(),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useClass: HashTranslateLoader,
        },
        missingTranslationHandler: {
          provide: MissingTranslationHandler,
          useClass: CustomMissingTranslationHandler,
        },
      }),
    ),
    provideRouter(
      appRoutes,
      withPreloading(AppRoutingPreloadStrategy),
      withRouterConfig({ onSameUrlNavigation: 'reload', paramsInheritanceStrategy: 'always' }),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' }),
    ),
    provideAppInitializer(() => inject(ViewportScroller).setOffset([0, 144])),
    provideEagerRouteStore(),
    provideStore(reducers, {
      runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false,
      },
    }),
    provideEffects(SapGlobalEffects, SapAssetsEffects),
    provideState(tableStareDataFeature),
    provideState(customFieldsReducer.name, customFieldsReducer.reducer),
    provideState(assetsFeature),
    provideState(tenantFeature),
    provideEffects(
      AttachmentsEffects,
      CustomFieldsEffects,
      AssetsEffects,
      TenantEffects,
      TableStoreEffects,
      RouteSyncEffects,
    ),
    ...environment.providers,
    CookieService,
    AppConfig,
    provideHttpClient(withInterceptorsFromDi()),
    provideSmaTrustedIframeDomains(environment.trustedDomain ?? []),
    { provide: LOCALE_ID, useFactory: (cookie: CookieService) => cookie.get('sma-web-lang-code') || 'en', deps: [CookieService] },
    { provide: WINDOW, useFactory: (): Window | null => windowFactory() },
    { provide: LOCAL_STORAGE, useFactory: (): Storage | null => getLocalStorage() },
    { provide: SESSION_STORAGE, useFactory: (): Storage | null => getSessionStorage() },
    provideAppInitializer(() => (initializeApp)(inject(AppConfig))()),
    ...(environment.version === 'local' ? [provideAppInitializer(() => initializeDevAuth()())] : []),
    provideAppInitializer(() => initializeWelcomeBranding()()),
    provideAppInitializer(() => initializeIdentity()()),
    provideAppInitializer(() => initializeAuthSessionKeepalive()()),
    provideAppInitializer(() => initializeDidomi(inject(DidomiService), inject(WelcomeBrandingLoaderService))()),
    provideAppInitializer(() => initializeDidomiRecovery(inject(DidomiService), inject(WelcomeBrandingLoaderService))()),
    provideAppInitializer(() => initializeFwaTracking(inject(FwaTrackingService), inject(WelcomeBrandingLoaderService))()),
    { provide: EventManager, useClass: CustomEventManager },
    // for responses order of interceptors flows out from the end to begin, so first in the list will be the last for response
    { provide: HTTP_INTERCEPTORS, useClass: LanguageHeaderInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ApiCacheInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorConvertInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: DeletionGuardInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: SilentTokenRefreshInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: BearerTokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CurrentTenantInterceptor, multi: true },
    {
      provide: SMA_VALIDATION_ERROR_MAPPER,
      useFactory: (formHelperService: FormHelperService): SmaValidationErrorMapper => {
        return (errors: ValidationErrors, context?: SmaValidationErrorContext): string | null => {
          return formHelperService.getErrorMessageFromErrors(errors, context);
        };
      },
      deps: [FormHelperService],
    },
    provideTableMemory(),
    provideFilterMemory(),
    provideVersionIntro(),
    // Web Pub Sub service data
    WebPubSubService,
    { provide: WEB_PUB_SUB_ENDPOINT, useValue: WEB_PUB_SUB_API_ENDPOINT },
  ],
};
